import type { GenerationJobRow } from "@/types/workspace"

export type BillingExportKind =
  | "company_operations"
  | "company_usage"
  | "platform_usage"
  | "promo_codes"
  | "promo_redemptions"
  | "promo_qr"
  | "promo_cards"

export type BillingExportScope = "company" | "platform"

export type BillingExportRow = {
  id: number
  kind: BillingExportKind
  status: string
  progress: number
  generation_job_id: number
  created_at: string
  requested_by_email: string | null
  period: string
  files_count: number | null
  download_path: string | null
}

export type BillingExportAccess = Record<BillingExportScope, boolean>

// Как BillingExport::JOB_KINDS на сервере.
export const BILLING_EXPORT_JOB_KINDS: Record<BillingExportKind, string> = {
  company_operations: "billing_operations_export",
  company_usage: "billing_usage_export",
  platform_usage: "billing_usage_export",
  promo_codes: "promo_codes_export",
  promo_redemptions: "promo_redemptions_export",
  promo_qr: "promo_qr_export",
  promo_cards: "promo_cards_export",
}

export const BILLING_EXPORTS_PATH: Record<BillingExportScope, string> = {
  company: "/company/billing/exports",
  platform: "/platform/billing/exports",
}

const ACTIVE_STATUSES = new Set(["queued", "processing"])

type ExportJob = Pick<GenerationJobRow, "id" | "job_kind" | "status">

export function isActiveExportStatus(status: string): boolean {
  return ACTIVE_STATUSES.has(status)
}

function jobKindsOf(kinds: readonly BillingExportKind[]): Set<string> {
  return new Set(kinds.map((kind) => BILLING_EXPORT_JOB_KINDS[kind]))
}

export function hasActiveExportJob(jobs: readonly ExportJob[], kinds: readonly BillingExportKind[]): boolean {
  const jobKinds = jobKindsOf(kinds)
  return jobs.some((job) => jobKinds.has(job.job_kind) && ACTIVE_STATUSES.has(job.status))
}

// Меняется при постановке, завершении и отмене выгрузок страницы — не на тиках прогресса.
export function exportJobsFingerprint(jobs: readonly ExportJob[], kinds: readonly BillingExportKind[]): string {
  const jobKinds = jobKindsOf(kinds)
  return jobs
    .filter((job) => jobKinds.has(job.job_kind))
    .map((job) => `${job.id}:${job.status}`)
    .sort()
    .join("|")
}

export function exportJobDownloadPath(
  job: Pick<GenerationJobRow, "status" | "billing_export_id" | "billing_export_scope">,
  access: BillingExportAccess,
): string | null {
  const id = job.billing_export_id
  const scope = job.billing_export_scope
  if (job.status !== "completed" || id == null || scope == null || !access[scope]) return null
  return `${BILLING_EXPORTS_PATH[scope]}/${id}/download`
}

export function canCancelExportJob(
  job: Pick<GenerationJobRow, "billing_export_id" | "billing_export_scope">,
  access: BillingExportAccess,
): boolean {
  if (job.billing_export_id == null) return true
  const scope = job.billing_export_scope
  return scope != null && access[scope]
}

type ReadyExportJob = Pick<
  GenerationJobRow,
  "id" | "job_kind" | "status" | "created_at" | "billing_export_id" | "billing_export_scope"
>

// Запас на расхождение часов браузера и сервера.
const CLOCK_SKEW_MS = 60_000

// Самая свежая готовая выгрузка страницы, запущенная после её открытия (since — мс).
export function latestReadyExportPath(
  jobs: readonly ReadyExportJob[],
  kinds: readonly BillingExportKind[],
  scope: BillingExportScope,
  since: number,
): string | null {
  const jobKinds = jobKindsOf(kinds)
  let latest: ReadyExportJob | null = null
  for (const job of jobs) {
    if (!jobKinds.has(job.job_kind) || job.status !== "completed" || job.billing_export_scope !== scope) continue
    if (!job.created_at || Date.parse(job.created_at) < since - CLOCK_SKEW_MS) continue
    if (!latest || job.id > latest.id) latest = job
  }
  return latest ? exportJobDownloadPath(latest, { company: true, platform: true }) : null
}
